import { useEffect, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { apiRequest, checkAdminSession, fetchBooks, fetchIssues, fetchTags, fetchUs, uploadMedia, type ApiIssue, type ApiTag, type ApiUs } from "./data/api";
import type { Book } from "./data/seed";

type AdminBook = Book & { coverKey?: string };
type BookForm = { slug: string; title: string; subtitle: string; description: string; coverKey: string; color: string; createdAt: string };
type IssueForm = { bookSlug: string; slug: string; title: string; description: string; publishedAt: string; imageKey: string; body: string; tags: string };
type TagForm = { slug: string; label: string };
type UsMomentForm = { date: string; title: string; note: string };
type UsPhotographForm = { imageKey: string; imageUrl?: string; alt: string; date: string; note: string };
type UsSettingsForm = { startDate: string; partnerA: string; partnerB: string; headingNote: string; heroImageKey: string };
type RunAction = (action: () => Promise<void>, success: string) => Promise<void>;

const today = () => new Date().toISOString().slice(0, 10);
const emptyBook = (): BookForm => ({ slug: "", title: "", subtitle: "", description: "", coverKey: "", color: "#d9d1c3", createdAt: today() });
const emptyIssue = (bookSlug = ""): IssueForm => ({ bookSlug, slug: "", title: "", description: "", publishedAt: today(), imageKey: "", body: "", tags: "" });
const emptyTag = (): TagForm => ({ slug: "", label: "" });
const emptyUsMoment = (): UsMomentForm => ({ date: today(), title: "", note: "" });
const emptyUsPhotograph = (overrides: Partial<UsPhotographForm> = {}): UsPhotographForm => ({ imageKey: "", alt: "", date: today(), note: "", ...overrides });
function usSettingsFromApi(us: ApiUs): UsSettingsForm {
	return {
		startDate: us.startDate, partnerA: us.partnerA, partnerB: us.partnerB, headingNote: us.headingNote, heroImageKey: us.heroImageKey,
	};
}
function momentsFromApi(us: ApiUs): UsMomentForm[] { return us.moments.map(({ date, title, note }) => ({ date, title, note })); }
function photographsFromApi(us: ApiUs): UsPhotographForm[] {
	return us.photographs.map(({ imageKey, image, alt, date, note }) => ({ imageKey, imageUrl: image, alt, date, note }));
}
function filenameLabel(name: string) {
	return name.replace(/\.[^/.]+$/, "").replace(/[-_]+/g, " ").trim() || "未命名照片";
}

function AdminImageInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState("");
	async function onFile(event: ChangeEvent<HTMLInputElement>) {
		const file = event.currentTarget.files?.[0];
		if (!file) return;
		setUploading(true); setError("");
		try { onChange((await uploadMedia(file)).key); }
		catch (reason) { setError(reason instanceof Error ? reason.message : "上传失败"); }
		finally { setUploading(false); event.currentTarget.value = ""; }
	}
	return <div className="admin-media-input"><input value={value} onChange={(event) => onChange(event.target.value)} placeholder="R2 object key" /><label className="admin-file-button">{uploading ? "上传中…" : "上传图片"}<input type="file" accept="image/*" onChange={onFile} /></label>{error && <small className="admin-error">{error}</small>}</div>;
}

function AdminBatchImageUpload({ onUploaded }: { onUploaded: (uploads: Array<{ key: string; url: string; name: string }>) => void }) {
	const [progress, setProgress] = useState({ done: 0, total: 0 });
	const [error, setError] = useState("");
	const uploading = progress.total > 0 && progress.done < progress.total;
	async function onFiles(event: ChangeEvent<HTMLInputElement>) {
		const files = Array.from(event.currentTarget.files ?? []);
		if (!files.length) return;
		setError("");
		setProgress({ done: 0, total: files.length });
		const uploads: Array<{ key: string; url: string; name: string }> = [];
		try {
			for (const [index, file] of files.entries()) {
				const result = await uploadMedia(file);
				uploads.push({ ...result, name: file.name });
				onUploaded([{ ...result, name: file.name }]);
				setProgress({ done: index + 1, total: files.length });
			}
		} catch (reason) {
			setError(reason instanceof Error ? reason.message : "批量上传失败");
			setProgress({ done: uploads.length, total: files.length });
		} finally {
			event.currentTarget.value = "";
			window.setTimeout(() => setProgress({ done: 0, total: 0 }), 1200);
		}
	}
	return <div className="admin-batch-upload">
		<label className="admin-upload-dropzone">
			<span className="admin-upload-icon" aria-hidden="true">＋</span>
			<strong>{uploading ? `正在上传 ${progress.done} / ${progress.total}` : "选择多张照片"}</strong>
			<small>支持 JPG、PNG、WebP，可一次选择多张</small>
			<input type="file" accept="image/*" multiple onChange={onFiles} disabled={uploading} />
		</label>
		{error && <small className="admin-error">{error}</small>}
	</div>;
}

function AdminShell({ children, notice, error, logout }: { children: ReactNode; notice: string; error: string; logout: () => Promise<void> }) {
	return <main className="admin-page">
		<header className="admin-header">
			<div><Link className="admin-brand" to="/admin">CQBLOG JOURNAL <span>/ ADMIN</span></Link><p className="admin-kicker">内容管理系统</p></div>
			<button type="button" onClick={logout}>退出登录</button>
		</header>
		<nav className="admin-nav" aria-label="管理导航">
			<NavLink to="/admin" end>总览</NavLink>
			<NavLink to="/admin/books">Books</NavLink>
			<NavLink to="/admin/issues">Issues</NavLink>
			<NavLink to="/admin/tags">Tags</NavLink>
			<NavLink to="/admin/us/overview">Us</NavLink>
			<a href="/" target="_blank" rel="noreferrer">查看网站 ↗</a>
		</nav>
		{notice && <p className="admin-notice" role="status">{notice}</p>}
		{error && <p className="admin-error" role="alert">{error}</p>}
		<div className="admin-content">{children}</div>
	</main>;
}

function PageHeading({ kicker, title, description, action }: { kicker: string; title: string; description: string; action?: ReactNode }) {
	return <div className="admin-page-heading"><div><p className="admin-kicker">{kicker}</p><h1>{title}</h1><p className="admin-description">{description}</p></div>{action}</div>;
}

function AdminDashboard({ books, issues, tags, us }: { books: AdminBook[]; issues: ApiIssue[]; tags: ApiTag[]; us: ApiUs }) {
	return <section className="admin-dashboard">
		<PageHeading kicker="CQBLOG / OVERVIEW" title="内容管理" description="从这里进入对应的内容层级。每个页面只处理一种资源。" />
		<div className="admin-summary-grid">
			<Link className="admin-summary-card" to="/admin/books"><strong>{books.length}</strong><span>Books</span><small>书籍与封面</small></Link>
			<Link className="admin-summary-card" to="/admin/issues"><strong>{issues.length}</strong><span>Issues</span><small>文章与章节</small></Link>
			<Link className="admin-summary-card" to="/admin/tags"><strong>{tags.length}</strong><span>Tags</span><small>统一标签</small></Link>
		<Link className="admin-summary-card" to="/admin/us/overview"><strong>{us.moments.length}</strong><span>Us</span><small>{us.photographs.length} 张照片</small></Link>
		</div>
		<div className="admin-quick-links"><p className="admin-kicker">QUICK ACTIONS</p><Link to="/admin/books/new">新建 Book <span>→</span></Link><Link to="/admin/issues/new">新建 Issue <span>→</span></Link><Link to="/admin/tags/new">新建 Tag <span>→</span></Link><Link to="/admin/us/overview">编辑 Us 页面 <span>→</span></Link></div>
	</section>;
}

function BookList({ books, run }: { books: AdminBook[]; run: RunAction }) {
	async function remove(book: AdminBook) {
		if (!window.confirm(`删除「${book.title}」以及其中的 Issue？`)) return;
		await run(async () => { await apiRequest(`/books/${encodeURIComponent(book.slug)}`, { method: "DELETE" }); }, "Book 已删除");
	}
	return <section>
		<PageHeading kicker="01 / BOOKS" title="Books" description="管理书架上的主题集合，以及每本书的封面和基本信息。" action={<Link className="admin-primary-link" to="/admin/books/new">新建 Book</Link>} />
		<div className="admin-record-list">{books.map((book) => <article className="admin-record" key={book.slug}>
			<div className="admin-record-cover">{book.cover && <img src={book.cover} alt="" />}</div>
			<div className="admin-record-main"><h2>{book.title}</h2><p>{book.subtitle} · {book.issues.length} Issues</p><small>{book.slug}</small></div>
			<div className="admin-record-actions"><Link to={`/admin/books/${encodeURIComponent(book.slug)}/edit`}>编辑</Link><button type="button" onClick={() => remove(book)}>删除</button></div>
		</article>)}</div>
	</section>;
}

function BookEditor({ books, run }: { books: AdminBook[]; run: RunAction }) {
	const { bookSlug } = useParams();
	const navigate = useNavigate();
	const book = books.find((item) => item.slug === bookSlug);
	const [form, setForm] = useState<BookForm>(() => book ? { slug: book.slug, title: book.title, subtitle: book.subtitle, description: book.description, coverKey: book.coverKey ?? "", color: book.color, createdAt: book.createdAt } : emptyBook());
	if (bookSlug && !book) return <AdminNotFound resource="Book" />;
	const editing = Boolean(bookSlug);
	async function save(event: FormEvent) {
		event.preventDefault();
		await run(async () => { await apiRequest(editing ? `/books/${encodeURIComponent(bookSlug!)}` : "/books", { method: editing ? "PATCH" : "POST", body: JSON.stringify(form) }); navigate("/admin/books"); }, editing ? "Book 已更新" : "Book 已创建");
	}
	const update = (key: keyof BookForm, value: string) => setForm((current) => ({ ...current, [key]: value }));
	return <section>
		<PageHeading kicker={editing ? "01 / BOOKS / EDIT" : "01 / BOOKS / NEW"} title={editing ? "编辑 Book" : "新建 Book"} description="Book 是内容的第一层级，Issue 会归属于这里。" />
		<form className="admin-form admin-editor-form" onSubmit={save}><div className="admin-form-grid"><label>Slug<input value={form.slug} onChange={(event) => update("slug", event.target.value)} required /></label><label>Title<input value={form.title} onChange={(event) => update("title", event.target.value)} required /></label><label>Subtitle<input value={form.subtitle} onChange={(event) => update("subtitle", event.target.value)} required /></label><label>创建日期<input type="date" value={form.createdAt} onChange={(event) => update("createdAt", event.target.value)} required /></label><label>颜色<input type="color" value={form.color} onChange={(event) => update("color", event.target.value)} /></label><label>封面<AdminImageInput value={form.coverKey} onChange={(value) => update("coverKey", value)} /></label></div><label>简介<textarea value={form.description} onChange={(event) => update("description", event.target.value)} required /></label><div className="admin-form-actions"><button className="admin-submit" type="submit">{editing ? "保存 Book" : "创建 Book"}</button><Link className="admin-cancel" to="/admin/books">取消</Link></div></form>
	</section>;
}

function IssueList({ issues, run }: { issues: ApiIssue[]; run: RunAction }) {
	async function remove(issue: ApiIssue) {
		if (!window.confirm(`删除「${issue.title}」？`)) return;
		await run(async () => { await apiRequest(`/issues/${encodeURIComponent(issue.slug)}`, { method: "DELETE" }); }, "Issue 已删除");
	}
	return <section>
		<PageHeading kicker="02 / ISSUES" title="Issues" description="管理每本 Book 里的文章章节、正文、配图和标签。" action={<Link className="admin-primary-link" to="/admin/issues/new">新建 Issue</Link>} />
		<div className="admin-record-list admin-issue-list">{issues.map((issue) => <article className="admin-record" key={issue.slug}>
			<div className="admin-record-cover">{issue.image && <img src={issue.image} alt="" />}</div>
			<div className="admin-record-main"><h2>{issue.title}</h2><p>{issue.bookTitle ?? issue.bookSlug} · {issue.date}</p><small>{issue.tags.map((tag) => tag.label).join(" / ") || "暂无标签"}</small></div>
			<div className="admin-record-actions"><Link to={`/admin/issues/${encodeURIComponent(issue.slug)}/edit`}>编辑</Link><button type="button" onClick={() => remove(issue)}>删除</button></div>
		</article>)}</div>
	</section>;
}

function IssueEditor({ books, tags, issues, run }: { books: AdminBook[]; tags: ApiTag[]; issues: ApiIssue[]; run: RunAction }) {
	const { issueSlug } = useParams();
	const navigate = useNavigate();
	const issue = issues.find((item) => item.slug === issueSlug);
	const [form, setForm] = useState<IssueForm>(() => issue ? { bookSlug: issue.bookSlug, slug: issue.slug, title: issue.title, description: issue.description, publishedAt: issue.date, imageKey: issue.imageKey ?? "", body: issue.body.join("\n\n"), tags: issue.tags.map((tag) => tag.label).join(", ") } : emptyIssue(books[0]?.slug ?? ""));
	if (issueSlug && !issue) return <AdminNotFound resource="Issue" />;
	const editing = Boolean(issueSlug);
	async function save(event: FormEvent) {
		event.preventDefault();
		const payload = { ...form, body: form.body.split(/\n\s*\n+/).map((part) => part.trim()).filter(Boolean), tags: form.tags.split(",").map((tag) => tag.trim()).filter(Boolean) };
		await run(async () => { await apiRequest(editing ? `/issues/${encodeURIComponent(issueSlug!)}` : "/issues", { method: editing ? "PATCH" : "POST", body: JSON.stringify(payload) }); navigate("/admin/issues"); }, editing ? "Issue 已更新" : "Issue 已创建");
	}
	const update = (key: keyof IssueForm, value: string) => setForm((current) => ({ ...current, [key]: value }));
	return <section>
		<PageHeading kicker={editing ? "02 / ISSUES / EDIT" : "02 / ISSUES / NEW"} title={editing ? "编辑 Issue" : "新建 Issue"} description="Issue 是 Book 里的单篇文章，标签会自动归入 Tag 管理。" />
		<form className="admin-form admin-editor-form" onSubmit={save}><div className="admin-form-grid"><label>Book<select value={form.bookSlug} onChange={(event) => update("bookSlug", event.target.value)} required><option value="">选择 Book</option>{books.map((book) => <option key={book.slug} value={book.slug}>{book.title}</option>)}</select></label><label>Slug<input value={form.slug} onChange={(event) => update("slug", event.target.value)} required /></label><label>标题<input value={form.title} onChange={(event) => update("title", event.target.value)} required /></label><label>发布日期<input type="date" value={form.publishedAt} onChange={(event) => update("publishedAt", event.target.value)} required /></label><label>图片<AdminImageInput value={form.imageKey} onChange={(value) => update("imageKey", value)} /></label><label>标签<input value={form.tags} onChange={(event) => update("tags", event.target.value)} placeholder={tags.length ? tags.map((tag) => tag.label).slice(0, 3).join(", ") : "用逗号分隔"} /></label></div><label>摘要<textarea value={form.description} onChange={(event) => update("description", event.target.value)} required /></label><label>Markdown 正文<textarea className="admin-body-input" value={form.body} onChange={(event) => update("body", event.target.value)} placeholder="支持标题、列表、引用、链接、图片等 Markdown 语法" required /></label><div className="admin-form-actions"><button className="admin-submit" type="submit">{editing ? "保存 Issue" : "创建 Issue"}</button><Link className="admin-cancel" to="/admin/issues">取消</Link></div></form>
	</section>;
}

function TagList({ tags, run }: { tags: ApiTag[]; run: RunAction }) {
	async function remove(tag: ApiTag) {
		if (!window.confirm(`删除 Tag「${tag.label}」？关联关系也会移除。`)) return;
		await run(async () => { await apiRequest(`/tags/${encodeURIComponent(tag.slug)}`, { method: "DELETE" }); }, "Tag 已删除");
	}
	return <section>
		<PageHeading kicker="03 / TAGS" title="Tags" description="统一维护文章标签。标签会从 Issue 编辑页被关联。" action={<Link className="admin-primary-link" to="/admin/tags/new">新建 Tag</Link>} />
		<div className="admin-tag-list">{tags.map((tag) => <article className="admin-tag-row" key={tag.slug}><div><strong>{tag.label}</strong><small>{tag.slug}</small></div><div className="admin-record-actions"><Link to={`/admin/tags/${encodeURIComponent(tag.slug)}/edit`}>编辑</Link><button type="button" onClick={() => remove(tag)}>删除</button></div></article>)}</div>
	</section>;
}

function TagEditor({ tags, run }: { tags: ApiTag[]; run: RunAction }) {
	const { tagSlug } = useParams();
	const navigate = useNavigate();
	const tag = tags.find((item) => item.slug === tagSlug);
	const [form, setForm] = useState<TagForm>(() => tag ? { slug: tag.slug, label: tag.label } : emptyTag());
	if (tagSlug && !tag) return <AdminNotFound resource="Tag" />;
	const editing = Boolean(tagSlug);
	async function save(event: FormEvent) {
		event.preventDefault();
		await run(async () => { await apiRequest(editing ? `/tags/${encodeURIComponent(tagSlug!)}` : "/tags", { method: editing ? "PATCH" : "POST", body: JSON.stringify(form) }); navigate("/admin/tags"); }, editing ? "Tag 已更新" : "Tag 已创建");
	}
	return <section>
		<PageHeading kicker={editing ? "03 / TAGS / EDIT" : "03 / TAGS / NEW"} title={editing ? "编辑 Tag" : "新建 Tag"} description="Tag 只描述内容主题，不改变 Book 与 Issue 的归属关系。" />
		<form className="admin-form admin-editor-form admin-tag-editor" onSubmit={save}><div className="admin-form-grid"><label>Slug<input value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} required /></label><label>名称<input value={form.label} onChange={(event) => setForm({ ...form, label: event.target.value })} required /></label></div><div className="admin-form-actions"><button className="admin-submit" type="submit">{editing ? "保存 Tag" : "创建 Tag"}</button><Link className="admin-cancel" to="/admin/tags">取消</Link></div></form>
	</section>;
}

function UsSectionNav({ us }: { us: ApiUs }) {
	return <div className="admin-us-navigation">
		<div><p className="admin-kicker">04 / US</p><p className="admin-us-navigation-copy">把页面拆成三步管理，保存时只影响当前区域。</p></div>
		<nav aria-label="Us 页面设置">
			<NavLink to="/admin/us/overview">基本信息</NavLink>
			<NavLink to="/admin/us/moments"><span>重要时刻</span><small>{us.moments.length}</small></NavLink>
			<NavLink to="/admin/us/photos"><span>照片</span><small>{us.photographs.length}</small></NavLink>
		</nav>
	</div>;
}

function UsOverviewEditor({ us, run }: { us: ApiUs; run: RunAction }) {
	const [form, setForm] = useState<UsSettingsForm>(() => usSettingsFromApi(us));
	useEffect(() => { setForm(usSettingsFromApi(us)); }, [us]);
	function update(key: keyof UsSettingsForm, value: string) { setForm((current) => ({ ...current, [key]: value })); }
	async function save(event: FormEvent) {
		event.preventDefault();
		await run(async () => { await apiRequest("/us", { method: "PUT", body: JSON.stringify(form) }); }, "基本信息已保存");
	}
	return <section className="admin-us-panel">
		<div className="admin-us-panel-heading"><div><p className="admin-kicker">STEP 01 / OVERVIEW</p><h2>基本信息</h2><p>设置 Us 页面的日期、名称和首页展示内容。</p></div><span className="admin-us-step">01</span></div>
		<form className="admin-form admin-us-form" onSubmit={save}>
			<div className="admin-form-grid">
				<label>开始日期<input type="date" value={form.startDate} onChange={(event) => update("startDate", event.target.value)} required /></label>
				<label>第一位<input value={form.partnerA} onChange={(event) => update("partnerA", event.target.value)} required /></label>
				<label>第二位<input value={form.partnerB} onChange={(event) => update("partnerB", event.target.value)} required /></label>
			</div>
			<label className="admin-us-wide-field">页头文案<input value={form.headingNote} onChange={(event) => update("headingNote", event.target.value)} required /></label>
			<div className="admin-us-hero-card"><div><p className="admin-kicker">HERO IMAGE</p><h3>首页图片</h3><p>用于 Us 页面开场区域。未设置时会使用默认封面。</p></div><AdminImageInput value={form.heroImageKey} onChange={(value) => update("heroImageKey", value)} /></div>
			<div className="admin-us-savebar"><span>修改后只保存基本信息</span><button className="admin-submit" type="submit">保存基本信息</button></div>
		</form>
	</section>;
}

function UsMomentsEditor({ us, run }: { us: ApiUs; run: RunAction }) {
	const [moments, setMoments] = useState<UsMomentForm[]>(() => momentsFromApi(us));
	useEffect(() => { setMoments(momentsFromApi(us)); }, [us]);
	function update(index: number, key: keyof UsMomentForm, value: string) { setMoments((current) => current.map((moment, itemIndex) => itemIndex === index ? { ...moment, [key]: value } : moment)); }
	function move(index: number, direction: -1 | 1) {
		setMoments((current) => { const next = [...current]; const target = index + direction; if (target < 0 || target >= next.length) return current; [next[index], next[target]] = [next[target], next[index]]; return next; });
	}
	async function save(event: FormEvent) {
		event.preventDefault();
		await run(async () => { await apiRequest("/us", { method: "PUT", body: JSON.stringify({ moments: moments.map((moment, sortOrder) => ({ ...moment, sortOrder })) }) }); }, "重要时刻已保存");
	}
	return <section className="admin-us-panel">
		<div className="admin-us-panel-heading"><div><p className="admin-kicker">STEP 02 / OUR STORY</p><h2>重要时刻</h2><p>记录关系里值得被记住的日期和故事，顺序会同步到前台时间线。</p></div><button type="button" onClick={() => setMoments((current) => [...current, emptyUsMoment()])}>添加时刻 <span>＋</span></button></div>
		<form className="admin-form admin-us-form" onSubmit={save}>
			{moments.length ? <div className="admin-us-moment-list">{moments.map((moment, index) => <article className="admin-us-moment-card" key={`moment-${index}`}>
				<div className="admin-us-card-index">{String(index + 1).padStart(2, "0")}</div>
				<div className="admin-us-card-fields">
					<label>日期<input type="date" value={moment.date} onChange={(event) => update(index, "date", event.target.value)} required /></label>
					<label>标题<input value={moment.title} onChange={(event) => update(index, "title", event.target.value)} required /></label>
					<label className="admin-us-note-field">记录<textarea value={moment.note} onChange={(event) => update(index, "note", event.target.value)} required /></label>
				</div>
				<div className="admin-us-card-actions"><button type="button" aria-label="上移" disabled={index === 0} onClick={() => move(index, -1)}>↑</button><button type="button" aria-label="下移" disabled={index === moments.length - 1} onClick={() => move(index, 1)}>↓</button><button className="admin-us-remove" type="button" onClick={() => setMoments((current) => current.filter((_, itemIndex) => itemIndex !== index))}>移除</button></div>
			</article>)}</div> : <div className="admin-us-empty"><strong>还没有重要时刻</strong><span>从第一个值得记住的日期开始。</span><button type="button" onClick={() => setMoments([emptyUsMoment()])}>添加第一个时刻</button></div>}
			<div className="admin-us-savebar"><span>{moments.length} 个时刻 · 可用右侧箭头调整顺序</span><button className="admin-submit" type="submit">保存重要时刻</button></div>
		</form>
	</section>;
}

function UsPhotosEditor({ us, run }: { us: ApiUs; run: RunAction }) {
	const [photographs, setPhotographs] = useState<UsPhotographForm[]>(() => photographsFromApi(us));
	useEffect(() => { setPhotographs(photographsFromApi(us)); }, [us]);
	function update(index: number, key: keyof UsPhotographForm, value: string) { setPhotographs((current) => current.map((photo, itemIndex) => itemIndex === index ? { ...photo, [key]: value } : photo)); }
	function move(index: number, direction: -1 | 1) {
		setPhotographs((current) => { const next = [...current]; const target = index + direction; if (target < 0 || target >= next.length) return current; [next[index], next[target]] = [next[target], next[index]]; return next; });
	}
	function addUploaded(uploads: Array<{ key: string; url: string; name: string }>) {
		setPhotographs((current) => [...current, ...uploads.map((upload) => { const label = filenameLabel(upload.name); return emptyUsPhotograph({ imageKey: upload.key, imageUrl: upload.url, alt: label, note: label }); })]);
	}
	async function save(event: FormEvent) {
		event.preventDefault();
		await run(async () => { await apiRequest("/us", { method: "PUT", body: JSON.stringify({ photographs: photographs.map((photo, sortOrder) => ({ objectKey: photo.imageKey, alt: photo.alt, date: photo.date, note: photo.note, sortOrder })) }) }); }, "照片已保存");
	}
	return <section className="admin-us-panel">
		<div className="admin-us-panel-heading"><div><p className="admin-kicker">STEP 03 / PHOTOGRAPHS</p><h2>照片</h2><p>一次选择多张照片，上传完成后再补充日期、说明和注记。</p></div><span className="admin-us-step">{String(photographs.length).padStart(2, "0")}</span></div>
		<form className="admin-form admin-us-form" onSubmit={save}>
			<AdminBatchImageUpload onUploaded={addUploaded} />
			{photographs.length ? <div className="admin-us-photo-list">{photographs.map((photo, index) => <article className="admin-us-photo-card" key={`photo-${index}`}>
				<div className="admin-us-photo-thumb">{photo.imageUrl ? <img src={photo.imageUrl} alt="" /> : <span>待上传</span>}</div>
				<div className="admin-us-photo-fields">
					<div className="admin-us-photo-file"><strong>{photo.alt || "未命名照片"}</strong><small>{photo.imageKey || "尚未选择图片"}</small></div>
					<div className="admin-us-photo-grid"><label>日期<input type="date" value={photo.date} onChange={(event) => update(index, "date", event.target.value)} required /></label><label>图片说明<input value={photo.alt} onChange={(event) => update(index, "alt", event.target.value)} required /></label></div>
					<label>注记<textarea value={photo.note} onChange={(event) => update(index, "note", event.target.value)} required /></label>
				</div>
				<div className="admin-us-card-actions"><button type="button" aria-label="上移" disabled={index === 0} onClick={() => move(index, -1)}>↑</button><button type="button" aria-label="下移" disabled={index === photographs.length - 1} onClick={() => move(index, 1)}>↓</button><button className="admin-us-remove" type="button" onClick={() => setPhotographs((current) => current.filter((_, itemIndex) => itemIndex !== index))}>移除</button></div>
			</article>)}</div> : <div className="admin-us-empty"><strong>还没有照片</strong><span>选择上方的批量上传区域，添加第一组照片。</span></div>}
			<div className="admin-us-savebar"><span>{photographs.length} 张照片 · 上传完成后记得保存</span><button className="admin-submit" type="submit">保存照片</button></div>
		</form>
	</section>;
}

function UsWorkspace({ us, run }: { us: ApiUs; run: RunAction }) {
	return <section className="admin-us-workspace">
		<PageHeading kicker="04 / US" title="Us" description="分开管理页面信息、重要时刻和照片，减少一次填写太多内容的负担。" />
		<UsSectionNav us={us} />
		<Routes>
			<Route index element={<Navigate to="overview" replace />} />
			<Route path="overview" element={<UsOverviewEditor us={us} run={run} />} />
			<Route path="moments" element={<UsMomentsEditor us={us} run={run} />} />
			<Route path="photos" element={<UsPhotosEditor us={us} run={run} />} />
			<Route path="*" element={<Navigate to="overview" replace />} />
		</Routes>
	</section>;
}

function AdminNotFound({ resource }: { resource: string }) {
	return <div className="admin-empty"><p>{resource} 不存在。</p><Link to="/admin">返回总览</Link></div>;
}

function AdminWorkspace({ logout }: { logout: () => Promise<void> }) {
	const [books, setBooks] = useState<AdminBook[]>([]);
	const [issues, setIssues] = useState<ApiIssue[]>([]);
	const [tags, setTags] = useState<ApiTag[]>([]);
	const [us, setUs] = useState<ApiUs | null>(null);
	const [ready, setReady] = useState(false);
	const [notice, setNotice] = useState("");
	const [error, setError] = useState("");
	async function refresh() {
		const [nextBooks, nextIssues, nextTags, nextUs] = await Promise.all([fetchBooks(), fetchIssues(), fetchTags(), fetchUs()]);
		setBooks(nextBooks as AdminBook[]); setIssues(nextIssues); setTags(nextTags); setUs(nextUs);
	}
	useEffect(() => { refresh().catch((reason) => setError(reason instanceof Error ? reason.message : "无法读取管理数据")).finally(() => setReady(true)); }, []);
	async function run(action: () => Promise<void>, success: string) {
		setNotice(""); setError("");
		try { await action(); await refresh(); setNotice(success); }
		catch (reason) { setError(reason instanceof Error ? reason.message : "操作失败"); }
	}
	if (!ready) return <AdminShell logout={logout} notice={notice} error={error}><p className="admin-status">正在读取管理数据…</p></AdminShell>;
	return <AdminShell logout={logout} notice={notice} error={error}><Routes>
		<Route index element={us ? <AdminDashboard books={books} issues={issues} tags={tags} us={us} /> : <p className="admin-status">正在读取 Us 数据…</p>} />
		<Route path="books" element={<BookList books={books} run={run} />} />
		<Route path="books/new" element={<BookEditor books={books} run={run} />} />
		<Route path="books/:bookSlug/edit" element={<BookEditor books={books} run={run} />} />
		<Route path="issues" element={<IssueList issues={issues} run={run} />} />
		<Route path="issues/new" element={<IssueEditor books={books} tags={tags} issues={issues} run={run} />} />
		<Route path="issues/:issueSlug/edit" element={<IssueEditor books={books} tags={tags} issues={issues} run={run} />} />
		<Route path="tags" element={<TagList tags={tags} run={run} />} />
		<Route path="tags/new" element={<TagEditor tags={tags} run={run} />} />
		<Route path="tags/:tagSlug/edit" element={<TagEditor tags={tags} run={run} />} />
			<Route path="us/*" element={us ? <UsWorkspace us={us} run={run} /> : <p className="admin-status">正在读取 Us 数据…</p>} />
		<Route path="*" element={<AdminNotFound resource="页面" />} />
	</Routes></AdminShell>;
}

export default function AdminPage() {
	const [authenticated, setAuthenticated] = useState<boolean | null>(null);
	const [token, setToken] = useState("");
	const [loginError, setLoginError] = useState("");
	const location = useLocation();
	const navigate = useNavigate();
	useEffect(() => { checkAdminSession().then(setAuthenticated).catch(() => setAuthenticated(false)); }, []);
	async function login(event: FormEvent) {
		event.preventDefault(); setLoginError("");
		try {
			window.localStorage.setItem("qblog_admin_token", token);
			await apiRequest("/auth/login", { method: "POST", body: JSON.stringify({ token }) });
			setAuthenticated(true); setToken("");
			const from = typeof location.state?.from === "string" ? location.state.from : "/admin";
			navigate(from, { replace: true, state: null });
		}
		catch (reason) { window.localStorage.removeItem("qblog_admin_token"); setLoginError(reason instanceof Error ? reason.message : "登录失败"); }
	}
	async function logout() { await apiRequest("/auth/logout", { method: "POST" }); window.localStorage.removeItem("qblog_admin_token"); setAuthenticated(false); }
	if (authenticated === null) return <main className="admin-page"><p className="admin-status">正在检查登录状态…</p></main>;
	if (!authenticated) return <main className="admin-page admin-login"><form className="admin-login-form" onSubmit={login}><p className="admin-kicker">CQBLOG JOURNAL / ADMIN</p><h1>管理后台</h1><label>管理令牌<input autoFocus type="password" value={token} onChange={(event) => setToken(event.target.value)} required /></label>{loginError && <p className="admin-error">{loginError}</p>}<button className="admin-submit" type="submit">登录</button><p className="admin-help">令牌由 Cloudflare Worker 的 ADMIN_TOKEN 提供。</p></form></main>;
	return <AdminWorkspace logout={logout} />;
}
